import { html, HTMLTemplateResult, nothing } from "lit";
import { RepeatRuleForm } from "./repeatRuleForm";
import { FieldInputEvent } from "@lib/types";
import { ScheduleRecurrence, SCHEDULE_RECURRENCE_LIST, SCHEDULE_RECURRENCE_TO_READABLE } from "../../types";
import moment from "moment";
import { DailyRepeatType, MonthlyRepeatType, RecurrenceEndType } from "../../types/recurrenceRule";
import { Weekday, WEEKDAY_LIST, WEEKDAY_TO_SHORT_READABLE } from "../../types/weekday";
import { keyed } from "lit/directives/keyed.js";

/** Hour repeat options template. */
function hourRepeatOptionsTemplate(this: RepeatRuleForm): HTMLTemplateResult {
    return html`
    ${this.form.field(
        {
            name: "rule.hourly.hourNumber",
        },
        field => html`
            <div class="repeat-option-container">
                Каждый
                <fw-input
                    type="number"
                    class="repeat-option-input"
                    min="1"
                    max="23"
                    .value=${field.state.value}
                    @fwInput=${(event: FieldInputEvent) => {
                        const newValue = event.detail.value;
                        field.handleChange(parseInt(newValue, 10));
                    }}
                    .disabled=${this.disabled}
                ></fw-input>
                час
            </div>
        `,
    )}
  `;
}

/** Day repeat options template. */
function dayRepeatOptionsTemplate(this: RepeatRuleForm): HTMLTemplateResult {
    return html`
    ${this.form.field(
        {
            name: "rule.daily.type",
        },
        field => html`
            <fw-radio-group
                value=${field.state.value}
                @fwChange="${(event: FieldInputEvent<DailyRepeatType>) => {
                    const newValue = event.detail.value;
                    if (newValue == null) {
                        return;
                    }
                    field.handleChange(newValue);
                }}"
            >
                <fw-radio value=${DailyRepeatType.DayNumber} .disabled=${this.disabled}>
                    ${this.form.field(
                        {
                            name: "rule.daily.dayNumber",
                        },
                        dayNumberField => html`
                        <div class="repeat-option-container">
                          Каждый
                            <fw-input
                                type="number"
                                class="repeat-option-input"
                                min="1"
                                max="7"
                                .value=${dayNumberField.state.value}
                                .disabled=${this.form.api.getFieldValue("rule.daily.type") !== DailyRepeatType.DayNumber || this.disabled}
                                @keyup=${(e: Event) => {
                                    e.stopPropagation();
                                }}
                                @fwInput=${(event: FieldInputEvent) => {
                                    event.stopPropagation();
                                    const newValue = event.detail.value;
                                    dayNumberField.handleChange(parseInt(newValue, 10));
                                }}
                            ></fw-input>
                            день
                        </div>
                      `,
                    )}
                </fw-radio>
                <fw-radio value=${DailyRepeatType.Weekdays} .disabled=${this.disabled}>
                    Каждый рабочий день
                </fw-radio>
                <fw-radio value=${DailyRepeatType.Weekends} .disabled=${this.disabled}>
                    Каждый нерабочий день
                </fw-radio>
            </fw-radio-group>
        `,
    )}
  `;
}

/** Week repeat options template. */
function weekRepeatOptionsTemplate(this: RepeatRuleForm): HTMLTemplateResult {
    return html`
    ${this.form.field(
        {
            name: "rule.weekly.weekNumber",
        },
        field => html`
            <div class="repeat-option-container">
                Каждую
                <fw-input
                  type="number"
                  class="repeat-option-input"
                  min="1"
                  max="4"
                  .value=${field.state.value}
                  @fwInput=${(event: FieldInputEvent) => {
                        const newValue = event.detail.value;
                        field.handleChange(parseInt(newValue, 10));
                    }}
                    .disabled=${this.disabled}
                ></fw-input>
                неделю
            </div>
        `,
    )}
    ${this.form.field(
        {
            name: "rule.weekly.days",
        },
        field => html`
            <div class="weekdays-container">
                ${WEEKDAY_LIST.map(weekday => html`
                    <fw-checkbox
                        .value=${weekday}
                        .checked=${field.state.value.includes(weekday)}
                        .disabled=${this.disabled}
                        @fwChange=${(event: FieldInputEvent<Weekday>) => {
                            const { value } = event.detail;
                            const oldValue = field.state.value;

                            const index = oldValue.indexOf(value);

                            if (index === -1) {
                                field.pushValue(value);
                            } else {
                                field.removeValue(index);
                            }
                        }}
                    >
                        ${WEEKDAY_TO_SHORT_READABLE[weekday]}
                    </fw-checkbox>
                `)}
            </div>
        `,
    )}
  `;
}

/** Month repeat options template. */
function monthRepeatOptionsTemplate(this: RepeatRuleForm): HTMLTemplateResult {
    return html`
    ${this.form.field(
        {
            name: "rule.monthly.type",
        },
        field => html`
            <fw-radio-group
                value=${field.state.value}
                @fwChange="${(event: FieldInputEvent<MonthlyRepeatType>) => {
                    const newValue = event.detail.value;
                    if (newValue == null) {
                        return;
                    }
                    field.handleChange(newValue);
                }}"
            >
                <fw-radio value=${MonthlyRepeatType.Weekday} .disabled=${this.disabled}>
                    <div class="repeat-option-container">
                        в
                        ${this.form.field(
                            {
                                name: "rule.monthly.weekday.weekdayNumber",
                            },
                            weekdayNumberField => html`
                                <fw-input
                                    type="number"
                                    class="repeat-option-input"
                                    min="1"
                                    max="4"
                                    .value=${weekdayNumberField.state.value}
                                    .disabled=${this.form.api.getFieldValue("rule.monthly.type") !== MonthlyRepeatType.Weekday ||
                                    this.disabled}
                                    @keyup=${(e: Event) => {
                                        e.stopPropagation();
                                    }}
                                    @fwInput=${(event: FieldInputEvent) => {
                                        event.stopPropagation();
                                        const newValue = event.detail.value;
                                        weekdayNumberField.handleChange(parseInt(newValue, 10));
                                    }}
                                ></fw-input>
                            `,
                        )}
                        ${this.form.field(
                            {
                                name: "rule.monthly.weekday.weekday",
                            },
                            weekdayField => html`
                                <fw-select
                                    class="form__month-day-select"
                                    .value=${weekdayField.state.value}
                                    .allowDeselect=${false}
                                    .disabled=${this.form.api.getFieldValue("rule.monthly.type") !== MonthlyRepeatType.Weekday ||
                                    this.disabled}
                                    @fwChange="${(event: FieldInputEvent<Weekday>) => {
                                        event.stopPropagation();
                                        const newValue = event.detail.value;
                                        weekdayField.handleChange(newValue);
                                    }}"
                                >
                                    ${WEEKDAY_LIST.map(weekday => html`
                                        <fw-select-option .value=${weekday}>${WEEKDAY_TO_SHORT_READABLE[weekday]}</fw-select-option>
                                    `)}
                            </fw-select>
                            `,
                        )}
                        каждого
                        ${this.form.field(
                            {
                                name: "rule.monthly.weekday.monthNumber",
                            },
                            monthNumberField => html`
                                <fw-input
                                    type="number"
                                    class="repeat-option-input"
                                    min="1"
                                    max="12"
                                    .value=${monthNumberField.state.value}
                                    .disabled=${this.form.api.getFieldValue("rule.monthly.type") !== MonthlyRepeatType.Weekday ||
                                    this.disabled}
                                    @keyup=${(e: Event) => {
                                        e.stopPropagation();
                                    }}
                                    @fwInput=${(event: FieldInputEvent) => {
                                        event.stopPropagation();
                                        const newValue = event.detail.value;
                                        monthNumberField.handleChange(parseInt(newValue, 10));
                                    }}
                                ></fw-input>
                            `,
                        )}
                        месяца
                    </div>
                </fw-radio>
                <fw-radio value=${MonthlyRepeatType.Day} .disabled=${this.disabled}>
                    <div class="repeat-option-container">
                        ${this.form.field(
                            {
                                name: "rule.monthly.day.dayNumber",
                            },
                            dayNumberField => html`
                                <fw-input
                                    type="number"
                                    class="repeat-option-input"
                                    min="1"
                                    max="31"
                                    .value=${dayNumberField.state.value}
                                    .disabled=${this.form.api.getFieldValue("rule.monthly.type") !== MonthlyRepeatType.Day || this.disabled}
                                    @keyup=${(e: Event) => {
                                        e.stopPropagation();
                                    }}
                                    @fwInput=${(event: FieldInputEvent) => {
                                        event.stopPropagation();
                                        const newValue = event.detail.value;
                                        dayNumberField.handleChange(parseInt(newValue, 10));
                                    }}
                                ></fw-input>
                            `,
                        )}
                        числа каждого
                        ${this.form.field(
                            {
                                name: "rule.monthly.day.monthNumber",
                            },
                            monthNumberField => html`
                                <fw-input
                                    type="number"
                                    class="repeat-option-input"
                                    min="1"
                                    max="12"
                                    .value=${monthNumberField.state.value}
                                    .disabled=${this.form.api.getFieldValue("rule.monthly.type") !== MonthlyRepeatType.Day || this.disabled}
                                    @keyup=${(e: Event) => {
                                        e.stopPropagation();
                                    }}
                                    @fwInput=${(event: FieldInputEvent) => {
                                        event.stopPropagation();
                                        const newValue = event.detail.value;
                                        monthNumberField.handleChange(parseInt(newValue, 10));
                                    }}
                                ></fw-input>
                            `,
                        )}
                        месяца
                    </div>
                </fw-radio>
            </fw-radio-group>
        `,
    )}
  `;
}

/** Gets repeat options tempalte. */
function getRepeatOptionsTemplate(this: RepeatRuleForm): HTMLTemplateResult | typeof nothing {
    switch (this.form.api.state.values.rule.recurrence) {
        case ScheduleRecurrence.Hourly:
            return hourRepeatOptionsTemplate.call(this);
        case ScheduleRecurrence.Daily:
            return dayRepeatOptionsTemplate.call(this);
        case ScheduleRecurrence.Weekly:
            return weekRepeatOptionsTemplate.call(this);
        case ScheduleRecurrence.Monthly:
            return monthRepeatOptionsTemplate.call(this);
        case ScheduleRecurrence.NoRepeat:
        default:
            return nothing;
    }
}

/** End form template. */
function endFormTemplate(this: RepeatRuleForm): HTMLTemplateResult {
    return html`
        <hr class="divider" />
        <fw-input
            label="Начало периода повторения"
            type="date"
            .value=${moment(this.startDate).format("YYYY-MM-DD")}
            disabled
        ></fw-input>
        <hr class="divider" />
        ${this.form.field(
            {
                name: "rule.end.type",
            },
            field => html`
                <fw-radio-group
                    label="Конец периода повторения"
                    value=${field.state.value}
                    @fwChange="${(event: FieldInputEvent<RecurrenceEndType>) => {
                        const newValue = event.detail.value;
                        if (newValue == null) {
                            return;
                        }
                        field.handleChange(newValue);
                    }}"
                >
                <fw-radio value=${RecurrenceEndType.Date} .disabled=${this.disabled}>
                    ${this.form.field(
                        {
                            name: "rule.end.date",
                        },
                        endRuleField => html`
                        <fw-datepicker
                            class="input datepicker"
                            placeholder="01.01.2025"
                            update-text="Применить"
                            .showErrorOnInvalidDate=${this.form.api.getFieldValue("rule.end.type") === RecurrenceEndType.Date}
                            .value=${moment(endRuleField.state.value).format("YYYY-MM-DD")}
                            .disabled=${this.form.api.getFieldValue("rule.end.type") !== RecurrenceEndType.Date || this.disabled}
                            .minDate=${moment(this.startDate).add(1, "d")
                                .format("YYYY-MM-DD")}
                            max-year="2099"
                            @keyup=${(e: Event) => {
                                e.stopPropagation();
                            }}
                            @fwChange=${(event: FieldInputEvent) => {
                                event.stopPropagation();
                                const newValue = event.detail.value;
                                this.handleDateChange(newValue);
                            }}
                        ></fw-datepicker>
                    `,
                    )}
                </fw-radio>
                <fw-radio value=${RecurrenceEndType.Never} .disabled=${this.disabled}>
                    Никогда
                </fw-radio>
            </fw-radio-group>
        `,
        )}
  `;
}

/** Repeat rule form template. */
export function repeatRuleFormTemplate(this: RepeatRuleForm): HTMLTemplateResult {
    return html`
        <form
            class="form"
            @submit=${this.handleSubmit.bind(this)}
        >
            ${this.form.field(
                {
                    name: "rule.recurrence",
                },
                field => keyed(field.state.value, html`
                    <fw-select
                        class="input recurrence-select"
                        label="Повтор"
                        value=${field.state.value}
                        placeholder="Повтор"
                        .allowDeselect=${false}
                        hoist
                        @fwChange="${(event: FieldInputEvent<ScheduleRecurrence>) => {
                            const newValue = event.detail.value;
                            field.handleChange(newValue);
                            void this.handleRecurrenceTypeChange();
                        }}"
                        .disabled=${this.disabled}
                    >
                        ${SCHEDULE_RECURRENCE_LIST.map(recurrence => html`
                            <fw-select-option value=${recurrence}>${SCHEDULE_RECURRENCE_TO_READABLE[recurrence]}</fw-select-option>`)
                        }
                    </fw-select>
                `),
            )}

            ${getRepeatOptionsTemplate.call(this)}
            
            ${
                this.form.api.getFieldValue("rule.recurrence") !== ScheduleRecurrence.NoRepeat ?
                    endFormTemplate.call(this) :
                    nothing
            }
    
            <div class="actions">
                <fw-button class="submit-button" type="submit">Применить</fw-button>
                <fw-button type="button" color="secondary" @click=${this.handleCancelButtonClick.bind(this)}>Отмена</fw-button>
            </div>
        </form>
  `;
}
